import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAiStatus } from '../contexts/AiStatusContext';
import { Search, MapPin, Tag, X, Sparkles, AlertCircle, User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize helper — strips Latvian garumzīmes / diacritics and lowercases.
const normalizeStr = (str) =>
  (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Canonical Latvian city names (matches backend LATVIAN_REGIONS keys).
// Only the nominative canonical form is listed; matching is done via diacritic
// normalization + prefix match so "liepaja", "liepājā", "liepājas" all match "liepaja".
const CITY_KEYS = [
  'riga', 'jurmala', 'liepaja', 'ventspils', 'daugavpils', 'rezekne', 'jelgava', 'jekabpils',
  'valmiera', 'ogre', 'tukums', 'talsi', 'saldus', 'kuldiga', 'dobele', 'bauska', 'aizkraukle',
  'aluksne', 'balvi', 'cesis', 'gulbene', 'kraslava', 'limbazi', 'ludza', 'madona', 'preili',
  'sigulda', 'smiltene', 'valka', 'marupe', 'olaine', 'salaspils', 'adazi', 'kekava',
  'ikskile', 'lielvarde', 'livani', 'aizpute', 'kandava', 'skrunda', 'grobina', 'dagda',
  'iecava', 'balozi', 'baldone', 'salacgriva', 'saulkrasti', 'durbe', 'priekule', 'pavilosta',
  'piltene', 'stende', 'sabile', 'valdemarpils', 'jaunjelgava', 'koknese', 'cesvaine', 'lubana',
  'varaklani', 'vilani', 'vilaka', 'karsava', 'zilupe', 'subate', 'viesite', 'akniste',
  'aloja', 'ape', 'mazsalaca', 'rujiena', 'staicele', 'straupe', 'seda', 'strenci',
];

// Try to map a single word (already normalized) to a canonical city key.
const matchCityWord = (word) => {
  if (!word || word.length < 3) return null;
  for (const key of CITY_KEYS) {
    // Allow prefix match of at least 4 chars so "liep" -> liepaja, "liepajas" -> liepaja.
    // Require the shorter of the two to be at least 4 chars to avoid false positives like "ap" -> "ape".
    const minLen = Math.min(key.length, word.length);
    if (minLen < 4) continue;
    const prefix = minLen;
    if (key.slice(0, prefix) === word.slice(0, prefix)) return key;
  }
  return null;
};

export default function SmartSearch({ taxonomy, onApply, initialQuery = '', regionNames = null }) {
  const { language } = useLanguage();
  const { enabled: aiEnabled } = useAiStatus();
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [userResults, setUserResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [detectedFilters, setDetectedFilters] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiHint, setAiHint] = useState(null);  // user-visible message when AI yields nothing
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Build search index from taxonomy
  const searchIndex = useRef([]);

  useEffect(() => {
    if (!taxonomy) return;
    const index = [];
    for (const [catKey, cat] of Object.entries(taxonomy)) {
      for (const [subKey, sub] of Object.entries(cat.subcategories || {})) {
        // Add subcategory
        index.push({
          text_lv: sub.label_lv,
          text_en: sub.label_en,
          category: catKey,
          subcategory: subKey,
          type: 'subcategory',
          keywords_lv: sub.label_lv.toLowerCase().split(/\s+/),
        });
        // Add types
        for (const [typeKey, typeInfo] of Object.entries(sub.types || {})) {
          index.push({
            text_lv: typeInfo.lv,
            text_en: typeInfo.en,
            category: catKey,
            subcategory: subKey,
            typeKey,
            type: 'type',
            age: typeInfo.age,
            keywords_lv: typeInfo.lv.toLowerCase().split(/[\s/]+/),
            keywords_en: typeInfo.en.toLowerCase().split(/[\s/]+/),
          });
        }
      }
    }
    searchIndex.current = index;
  }, [taxonomy]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const detectFromQuery = (text) => {
    // A query starting with "@" is a people search — show matching users only.
    if ((text || '').trim().startsWith('@')) {
      setSuggestions([]);
      setCitySuggestions([]);
      setDetectedFilters(null);
      return;
    }
    if (!text || text.length < 2) {
      setSuggestions([]);
      setCitySuggestions([]);
      setDetectedFilters(null);
      return;
    }

    const lower = normalizeStr(text).trim();
    const words = lower.split(/\s+/).filter(Boolean);

    // Detect city/region (diacritic-insensitive, prefix match for inflected forms)
    let detectedRegion = null;
    const cityWords = new Set();
    for (const word of words) {
      const key = matchCityWord(word);
      if (key) {
        detectedRegion = key;
        cityWords.add(word);
        break;
      }
    }

    // Build a list of ALL city keys that match any word as prefix, for the dropdown.
    // Useful when user types "liep" → suggest Liepāja; "jel" → Jelgava, etc.
    const cityHits = new Set();
    for (const word of words) {
      if (!word || word.length < 3) continue;
      for (const key of CITY_KEYS) {
        if (key.startsWith(word) || word.startsWith(key.slice(0, Math.min(5, key.length)))) {
          cityHits.add(key);
        }
      }
    }
    setCitySuggestions(Array.from(cityHits).slice(0, 4));

    // Search taxonomy for matches (diacritic-insensitive)
    const matches = [];
    const searchWords = words.filter(w => !cityWords.has(w));

    for (const item of searchIndex.current) {
      const itemWords = [
        ...(item.keywords_lv || []).map(normalizeStr),
        ...(item.keywords_en || []).map(normalizeStr),
      ];
      const itemText = normalizeStr(`${item.text_lv} ${item.text_en}`);

      let score = 0;
      for (const sw of searchWords) {
        if (!sw || sw.length < 3) continue;
        // Strong match: the full search word appears as a substring of the item text.
        if (itemText.includes(sw)) {
          score += 3;
          continue;
        }
        // Weak match: a 5+ char prefix is shared between query and an item keyword.
        // (3-char prefix was too loose — "marcipans" spuriously matched "maratoni" / "markets".)
        if (sw.length < 5) continue;
        for (const iw of itemWords) {
          if (iw.length < 5) continue;
          const prefixLen = 5;
          if (iw.slice(0, prefixLen) === sw.slice(0, prefixLen)) {
            score += 1;
            break; // at most one weak match per search word per item
          }
        }
      }

      if (score > 0) {
        matches.push({ ...item, score });
      }
    }

    // Sort by score and take top 6
    matches.sort((a, b) => b.score - a.score);
    setSuggestions(matches.slice(0, 6));

    // Auto-detect filters from best match — require a strong match (full substring
    // of a search word inside the item text). Multiple weak prefix matches alone
    // are not enough to auto-apply a category filter.
    if (matches.length > 0 && matches[0].score >= 3) {
      const best = matches[0];
      setDetectedFilters({
        category: best.category,
        subcategory: best.subcategory,
        type: best.typeKey,
        region: detectedRegion,
        label: language === 'lv' ? best.text_lv : best.text_en,
        cityWords,
      });
    } else if (detectedRegion) {
      setDetectedFilters({ region: detectedRegion, cityWords });
    } else {
      setDetectedFilters(null);
    }

    setShowSuggestions(matches.length > 0 || !!detectedRegion || cityHits.size > 0);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    detectFromQuery(val);
  };

  // People search: when the query starts with "@", look up users by handle/name.
  useEffect(() => {
    const q = query.trim();
    if (!q.startsWith('@')) { setUserResults([]); return; }
    const handle = q.slice(1).trim();
    if (handle.length < 2) { setUserResults([]); return; }
    let active = true;
    const tid = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/user-search?q=${encodeURIComponent(handle)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (active) { setUserResults(data.users || []); setShowSuggestions(true); }
      } catch { /* ignore */ }
    }, 250);
    return () => { active = false; clearTimeout(tid); };
  }, [query]);

  const handleUserClick = (u) => {
    setShowSuggestions(false);
    setQuery('');
    setUserResults([]);
    navigate(`/lietotajs/${u.id}`);
  };

  // Navigate to listings OR apply filter inline via callback (used on ListingsPage)
  const goTo = (params) => {
    if (typeof onApply === 'function') {
      // Sync local query to the final search term so the input reflects reality
      setQuery(params.get('search') || '');
      setDetectedFilters(null);
      setSuggestions([]);
      setCitySuggestions([]);
      onApply(params);
    } else {
      navigate(`/listings?${params.toString()}`);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    const params = new URLSearchParams();
    params.set('category', suggestion.category);
    if (suggestion.category === 'event') {
      params.set('event_subcategory', suggestion.subcategory);
      if (suggestion.typeKey) params.set('event_type', suggestion.typeKey);
    } else if (suggestion.category === 'service') {
      params.set('service_subcategory', suggestion.subcategory);
      if (suggestion.typeKey) params.set('service_type', suggestion.typeKey);
    } else if (suggestion.category === 'shop') {
      params.set('subcategory', suggestion.subcategory);
    }
    if (detectedFilters?.region) params.set('region', detectedFilters.region);
    goTo(params);
  };

  const handleCityClick = (cityKey) => {
    setShowSuggestions(false);
    const params = new URLSearchParams();
    params.set('region', cityKey);
    // Preserve any text that isn't a prefix/inflection of the clicked city.
    const cleaned = query.trim().split(/\s+/)
      .filter(w => {
        const n = normalizeStr(w);
        if (!n) return false;
        // Drop the word if it resolves to this city OR prefixes the city key.
        const resolved = matchCityWord(n);
        if (resolved === cityKey) return false;
        if (n.length >= 3 && (cityKey.startsWith(n) || n.startsWith(cityKey.slice(0, Math.min(5, cityKey.length))))) return false;
        return true;
      })
      .join(' ').trim();
    if (cleaned) params.set('search', cleaned);
    goTo(params);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    // "@..." is a people search — open the first matching user on Enter.
    if (query.trim().startsWith('@')) {
      if (userResults.length > 0) handleUserClick(userResults[0]);
      return;
    }
    const params = new URLSearchParams();

    // If a region was detected, strip that word from the free-text search so we
    // don't double-filter (e.g., user typing "Liepaja" should set region=liepaja
    // without filtering titles by the literal word "Liepaja").
    let cleanedQuery = query.trim();
    const cityWords = detectedFilters?.cityWords;
    if (cityWords && cityWords.size > 0) {
      cleanedQuery = cleanedQuery
        .split(/\s+/)
        .filter(w => !cityWords.has(normalizeStr(w)))
        .join(' ')
        .trim();
    }

    if (cleanedQuery) params.set('search', cleanedQuery);
    // Only apply broad filters on free-text submit (category + region). Narrow
    // taxonomy filters (service_subcategory / event_subcategory) are applied
    // ONLY when the user explicitly picks a suggestion — see handleSuggestionClick.
    if (detectedFilters?.category) params.set('category', detectedFilters.category);
    if (detectedFilters?.region) params.set('region', detectedFilters.region);
    goTo(params);
  };

  // AI parse — POST the raw query to /api/ai/search/parse, surface the result
  // as a confirmable banner. Only applies when the user clicks "Apply" so they
  // never get a surprise filter change.
  const handleAiSearch = async () => {
    const text = query.trim();
    if (!text || aiLoading) return;
    setShowSuggestions(false);
    setAiLoading(true);
    setAiResult(null);
    setAiHint(null);
    try {
      const res = await fetch(`${API}/ai/search/parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: text }),
      });
      if (res.status === 429) {
        setAiHint({ kind: 'warning', text: language === 'lv'
          ? 'Pārāk daudz AI pieprasījumu. Lūdzu, mēģini pēc minūtes.'
          : 'Too many AI requests. Try again in a minute.' });
        return;
      }
      if (!res.ok) throw new Error('AI nepieejams');
      const data = await res.json();
      const filters = data?.filters || {};
      if (Object.keys(filters).length === 0) {
        // Differentiate: upstream error vs model truly found nothing.
        if (data?.error) {
          setAiHint({ kind: 'warning', text: language === 'lv'
            ? 'AI šobrīd nav pieejams — meklējam pēc teksta.'
            : 'AI unavailable right now — falling back to keyword search.' });
        } else {
          setAiHint({ kind: 'info', text: language === 'lv'
            ? 'AI neatpazina struktūru — meklējam pēc teksta.'
            : 'AI found no structured filters — searching by keyword.' });
        }
        const params = new URLSearchParams();
        params.set('search', text);
        goTo(params);
      } else {
        setAiResult({ filters, confidence: data.confidence || 0 });
      }
    } catch (err) {
      setAiHint({ kind: 'warning', text: language === 'lv'
        ? 'AI šobrīd nav pieejams — meklējam pēc teksta.'
        : 'AI unavailable — falling back to keyword search.' });
      const params = new URLSearchParams();
      params.set('search', text);
      goTo(params);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiResult = () => {
    if (!aiResult) return;
    const { filters } = aiResult;
    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.subcategory) params.set('subcategory', filters.subcategory);
    if (filters.product_type) params.set('product_type', filters.product_type);
    if (filters.business_type) params.set('business_type', filters.business_type);
    if (filters.rental_type) params.set('rental_type', filters.rental_type);
    if (filters.region) params.set('region', filters.region);
    if (filters.price_min) params.set('price_min', String(filters.price_min));
    if (filters.price_max) params.set('price_max', String(filters.price_max));
    if (Array.isArray(filters.keywords) && filters.keywords.length > 0) {
      params.set('search', filters.keywords.join(' '));
    }
    setAiResult(null);
    goTo(params);
  };

  const getCategoryLabel = (cat) => {
    const labels = { shop: 'Veikals', service: 'Pakalpojums', event: 'Pasākums', project: 'Projekts', business: 'Uzņēmums' };
    return labels[cat] || cat;
  };

  return (
    <form onSubmit={handleSubmit} className="relative max-w-2xl mx-auto" data-testid="smart-search">
      <div className="flex gap-2">
        <div className="relative flex-1" ref={inputRef}>
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C5C5C]" />
          <Input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => { if (suggestions.length > 0 || userResults.length > 0) setShowSuggestions(true); }}
            placeholder={language === 'lv' ? 'Meklēt produktus, pakalpojumus, @lietotājus...' : 'Search products, services, @people...'}
            className="pl-11 pr-4 h-13 text-base bg-white border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736] rounded-xl"
            data-testid="smart-search-input"
          />
          {query && (
            <button type="button" onClick={() => { setQuery(''); setSuggestions([]); setUserResults([]); setDetectedFilters(null); setShowSuggestions(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-[#121212]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button type="submit" className="h-13 px-6 bg-[#D97736] hover:bg-[#C06528] text-white rounded-xl" data-testid="smart-search-btn">
          <Search className="w-5 h-5" />
        </Button>
        {aiEnabled && (
          <Button
            type="button"
            onClick={handleAiSearch}
            disabled={aiLoading || !query.trim()}
            className="h-13 px-4 bg-gradient-to-br from-[#7C3AED] to-[#D97736] hover:opacity-90 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            title={language === 'lv' ? 'Meklē ar AI palīgu — saprot frāzes ar cenu, reģionu un kategoriju' : 'AI-assisted search — understands price/region/category in plain language'}
            data-testid="smart-search-ai-btn"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            {aiLoading ? '…' : 'AI'}
          </Button>
        )}
      </div>

      {/* AI-parsed result preview — user confirms before filters apply. */}
      {aiResult && (
        <div className="mt-2 p-3 bg-gradient-to-r from-[#7C3AED]/10 to-[#D97736]/10 border border-[#7C3AED]/30 rounded-xl flex items-center justify-between gap-3" data-testid="ai-result-banner">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-[#7C3AED]" />
              <span className="text-xs font-semibold text-[#7C3AED] uppercase tracking-wider">
                {language === 'lv' ? 'AI atpazina' : 'AI detected'}
              </span>
              {aiResult.confidence > 0 && (
                <span className="text-[10px] text-[#5C5C5C]">
                  {Math.round(aiResult.confidence * 100)}%
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {aiResult.filters.category && (
                <Badge variant="outline" className="text-xs bg-white">
                  <Tag className="w-3 h-3 mr-1" />
                  {getCategoryLabel(aiResult.filters.category)}
                </Badge>
              )}
              {aiResult.filters.region && (
                <Badge variant="outline" className="text-xs bg-white">
                  <MapPin className="w-3 h-3 mr-1" />
                  {regionNames?.[aiResult.filters.region] || aiResult.filters.region}
                </Badge>
              )}
              {(aiResult.filters.price_min || aiResult.filters.price_max) && (
                <Badge variant="outline" className="text-xs bg-white">
                  {aiResult.filters.price_min ? `€${aiResult.filters.price_min}` : '€0'}
                  {' – '}
                  {aiResult.filters.price_max ? `€${aiResult.filters.price_max}` : '∞'}
                </Badge>
              )}
              {Array.isArray(aiResult.filters.keywords) && aiResult.filters.keywords.map((kw, i) => (
                <Badge key={`kw-${i}`} variant="outline" className="text-xs bg-white">
                  {kw}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button type="button" size="sm" variant="ghost" onClick={() => setAiResult(null)} className="h-8 px-2 text-[#5C5C5C]" data-testid="ai-result-dismiss">
              <X className="w-3.5 h-3.5" />
            </Button>
            <Button type="button" size="sm" onClick={applyAiResult} className="h-8 bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="ai-result-apply">
              {language === 'lv' ? 'Pielietot' : 'Apply'}
            </Button>
          </div>
        </div>
      )}

      {/* Compact inline hint when the AI run yielded nothing usable (rate-limited,
          upstream error, or empty filters). Stays visible until the user types
          a new query or dismisses it. */}
      {aiHint && !aiResult && (
        <div
          className={`mt-2 px-3 py-2 rounded-lg flex items-start gap-2 text-xs ${
            aiHint.kind === 'warning'
              ? 'bg-amber-50 border border-amber-200 text-amber-800'
              : 'bg-slate-50 border border-slate-200 text-slate-700'
          }`}
          data-testid="ai-hint-banner"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="flex-1">{aiHint.text}</span>
          <button
            type="button"
            onClick={() => setAiHint(null)}
            className="text-[10px] uppercase tracking-wider opacity-70 hover:opacity-100"
            data-testid="ai-hint-dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Detected filters preview */}
      {detectedFilters && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <Sparkles className="w-3.5 h-3.5 text-[#D97736]" />
          {detectedFilters.category && (
            <Badge variant="outline" className="text-xs bg-[#D97736]/10 text-[#D97736] border-[#D97736]/30">
              <Tag className="w-3 h-3 mr-1" />
              {getCategoryLabel(detectedFilters.category)}
            </Badge>
          )}
          {detectedFilters.label && (
            <Badge variant="outline" className="text-xs bg-[#4A6B53]/10 text-[#4A6B53] border-[#4A6B53]/30">
              {detectedFilters.label}
            </Badge>
          )}
          {detectedFilters.region && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
              <MapPin className="w-3 h-3 mr-1" />
              {detectedFilters.region}
            </Badge>
          )}
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && (suggestions.length > 0 || citySuggestions.length > 0 || userResults.length > 0) && (
        <div ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E4E0] rounded-xl shadow-lg z-50 overflow-hidden max-h-96 overflow-y-auto"
          data-testid="smart-suggestions">
          {userResults.length > 0 && (
            <div className="border-b border-[#E5E4E0]">
              <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[#5C5C5C]">
                {language === 'lv' ? 'Lietotāji' : 'People'}
              </div>
              {userResults.map((u) => (
                <button
                  key={`user-${u.id}`}
                  type="button"
                  onClick={() => handleUserClick(u)}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#F3F2EE] flex items-center gap-3 transition-colors"
                  data-testid={`user-suggestion-${u.id}`}
                >
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#4A6B53]/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-[#4A6B53]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#121212] truncate">{u.public_name}</p>
                    <p className="text-xs text-[#5C5C5C] truncate">
                      {language === 'lv' ? 'Atvērt profilu' : 'Open profile'}
                      {u.region && <span className="ml-1">· {u.region}</span>}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {citySuggestions.length > 0 && (
            <div className="border-b border-[#E5E4E0]">
              <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[#5C5C5C]">
                {language === 'lv' ? 'Pilsētas' : 'Cities'}
              </div>
              {citySuggestions.map((cityKey) => (
                <button
                  key={`city-${cityKey}`}
                  type="button"
                  onClick={() => handleCityClick(cityKey)}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#F3F2EE] flex items-center gap-3 transition-colors"
                  data-testid={`city-suggestion-${cityKey}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#121212] truncate">
                      {regionNames?.[cityKey] || cityKey.charAt(0).toUpperCase() + cityKey.slice(1)}
                    </p>
                    <p className="text-xs text-[#5C5C5C]">
                      {language === 'lv' ? 'Filtrēt pēc reģiona' : 'Filter by region'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {suggestions.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[#5C5C5C]">
                {language === 'lv' ? 'Kategorijas' : 'Categories'}
              </div>
              {suggestions.map((s, i) => (
                <button
                  key={`${s.category}-${s.subcategory}-${s.typeKey || i}`}
                  type="button"
                  onClick={() => handleSuggestionClick(s)}
                  className="w-full text-left px-4 py-3 hover:bg-[#F3F2EE] flex items-center gap-3 border-b border-[#E5E4E0] last:border-0 transition-colors"
                  data-testid={`category-suggestion-${s.category}-${s.subcategory}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-[#D97736]/10 flex items-center justify-center flex-shrink-0">
                    <Tag className="w-4 h-4 text-[#D97736]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#121212] truncate">
                      {language === 'lv' ? s.text_lv : s.text_en}
                    </p>
                    <p className="text-xs text-[#5C5C5C]">
                      {getCategoryLabel(s.category)}
                      {s.age && s.age !== '0+' && <span className="ml-1 text-amber-600">({s.age})</span>}
                    </p>
                  </div>
                  {detectedFilters?.region && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      <MapPin className="w-2.5 h-2.5 mr-0.5" />
                      {detectedFilters.region}
                    </Badge>
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </form>
  );
}
